import * as Yup from 'yup';

import {
  CustomFieldInterface,
  CustomFieldsEnum,
} from 'store/reducers/contatos';
import { TiposDeCabecalhoMensagemInterativaEnum } from 'store/reducers/mensagens';
import {
  FormatosDeCabecalhoMensagemTemplateEnum,
  MessageHeaderFormatsTemplate,
} from 'store/reducers/templates';

export const MAX_FILE_SIZES = {
  [MessageHeaderFormatsTemplate.IMAGEM]: 5 * 1024 * 1024,
  [MessageHeaderFormatsTemplate.VIDEO]: 16 * 1024 * 1024,
  [MessageHeaderFormatsTemplate.DOCUMENTO]: 100 * 1024 * 1024,
};

export const FILE_SIZE_ERROR_MESSAGES = {
  [MessageHeaderFormatsTemplate.IMAGEM]: 'A imagem deve ter no máximo 5MB.',
  [MessageHeaderFormatsTemplate.VIDEO]: 'O vídeo deve ter no máximo 16MB.',
  [MessageHeaderFormatsTemplate.DOCUMENTO]:
    'O documento deve ter no máximo 100MB.',
};

export const alterarSenhaSchema = Yup.object().shape({
  old_password: Yup.string()
    .min(6, 'A senha deve ter no mínimo 6 caracteres.')
    .required('Digite sua senha atual.'),
  new_password: Yup.string()
    .min(6, 'A senha deve ter no mínimo 6 caracteres.')
    .required('Digite sua nova senha.'),
  new_password_confirmation: Yup.string()
    .oneOf([Yup.ref('new_password'), null], 'As senhas não correspondem.')
    .required('Digite sua nova senha de novo'),
});

const customFieldValidators: Record<CustomFieldsEnum, Yup.AnySchema> = {
  [CustomFieldsEnum.CNPJ]: Yup.string().min(18, 'CNPJ inválido').nullable(),
  [CustomFieldsEnum.CPF]: Yup.string().min(14, 'CPF inválido').nullable(),
  [CustomFieldsEnum.EMAIL]: Yup.string().email('E-mail inválido').nullable(),
  [CustomFieldsEnum.LOGICAL]: Yup.boolean().nullable(),
  [CustomFieldsEnum.MONEY]: Yup.string().nullable(),
  [CustomFieldsEnum.INTEGER]: Yup.number().nullable(),
  [CustomFieldsEnum.SHORT_TEXT]: Yup.string().nullable(),
  [CustomFieldsEnum.LONG_TEXT]: Yup.string().nullable(),
  [CustomFieldsEnum.URL]: Yup.string().url('URL inválida').nullable(),
  [CustomFieldsEnum.DATE]: Yup.date().typeError('Data inválida').nullable(),
};

export function buildDynamicFormikSchema(customFields: CustomFieldInterface[]) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dynamicInitialValues: Record<string, any> = {};
  const dynamicShape: Record<string, Yup.AnySchema> = {};

  customFields.forEach(field => {
    const validator =
      customFieldValidators[field.type as CustomFieldsEnum] || Yup.mixed();

    dynamicShape[field.id] = validator.notRequired();

    switch (field.type) {
      case CustomFieldsEnum.LOGICAL:
        dynamicInitialValues[field.id] = field.value ?? false;
        break;
      case CustomFieldsEnum.INTEGER:
      case CustomFieldsEnum.DATE:
        dynamicInitialValues[field.id] = field.value ?? null;
        break;
      default:
        dynamicInitialValues[field.id] = field.value ?? '';
        break;
    }
  });

  return {
    dynamicInitialValues,
    dynamicSchema: Yup.object().shape(dynamicShape),
  };
}

export const adicionarContatoSchema = Yup.object().shape({
  nome: Yup.string().required('Digite o nome.'),
  contato: Yup.string()
    .min(5, 'O contato deve ter pelo menos 5 digitos')
    .required('Digite o contato'),
  observacao: Yup.string()
    .max(255, 'O limite máximo de caracteres é de 255 caracteres')
    .nullable(),
  // tipoCanal: Yup.string().required('Selecione o tipo do canal.'),
});

export const alterarContatoSchema = Yup.object().shape({
  nome: Yup.string().required('Digite o nome.'),
  observacao: Yup.string()
    .max(255, 'O limite máximo de caracteres é de 255 caracteres')
    .nullable(),
});

export const alterarContatoV2Schema = Yup.object().shape({
  name: Yup.string().required('Digite o nome.'),
  observation: Yup.string()
    .max(255, 'O limite máximo de caracteres é de 255 caracteres')
    .nullable(),
});

export const corrigirContatoSchema = Yup.object().shape({
  contato: Yup.string()
    .min(5, 'O contato deve ter pelo menos 5 digitos')
    .required('Digite o número do contato.'),
});

export const iniciarContatoSchema = Yup.object().shape({
  canal: Yup.string().required('Selecione o canal.'),
  equipe: Yup.string().required('Selecione a equipe.'),
});

export const definirEquipePadraoSchema = Yup.object().shape({
  equipeId: Yup.string().required('Selecione a equipe.'),
  usuarioId: Yup.string().nullable(),
});

export const criarAgendamentoSchema = Yup.object().shape({
  canalId: Yup.string().required('Selecione o canal.'),
  equipeId: Yup.string().required('Selecione a equipe.'),
  agendarParaDate: Yup.string()
    .test(
      'agendarPara',
      'Informe uma data válida',
      value => value !== 'Invalid Date',
    )
    .required('Selecione uma data válida')
    .nullable(),
  agendarParaTime: Yup.string()
    .test(
      'agendarPara',
      'Informe uma hora válida',
      value => value !== 'Invalid Date',
    )
    .required('Selecione uma hora válida')
    .nullable(),
  mensagem: Yup.string()
    .max(255, 'O limite máximo de caracteres é de 255 caracteres')
    .required(),
  comentario: Yup.string()
    .max(255, 'O limite máximo de caracteres é de 255 caracteres')
    .nullable(),
});

export const alterarDataAgendamentoSchema = Yup.object().shape({
  agendarParaDate: Yup.string()
    .test(
      'agendarPara',
      'Informe uma data válida',
      value => value !== 'Invalid Date',
    )
    .required('Selecione uma data válida')
    .nullable(),
  agendarParaTime: Yup.string()
    .test(
      'agendarPara',
      'Informe uma hora válida',
      value => value !== 'Invalid Date',
    )
    .required('Selecione uma hora válida')
    .nullable(),
});

export const mensagemDeTagInstagramSchema = Yup.object().shape({
  mensagem: Yup.string()
    .max(2000, 'O limite máximo de caracteres é de 2000 caracteres')
    .required(),
});

export const criarMensagemDeUrlCta = Yup.object().shape({
  tipoCabecalho: Yup.string()
    .nullable()
    .oneOf([...Object.values(TiposDeCabecalhoMensagemInterativaEnum), null]),

  textoCabecalho: Yup.string().when('tipoCabecalho', {
    is: TiposDeCabecalhoMensagemInterativaEnum.TEXTO,
    then: schema => schema.required('O texto do cabeçalho é obrigatório'),
    otherwise: schema => schema.strip(),
  }),

  imagemCabecalho: Yup.string().when('tipoCabecalho', {
    is: TiposDeCabecalhoMensagemInterativaEnum.IMAGEM,
    then: schema =>
      schema
        .url('A URL deve ser válida')
        .required('A URL da imagem do cabeçalho é obrigatória'),
    otherwise: schema => schema.strip(),
  }),

  videoCabecalho: Yup.string().when('tipoCabecalho', {
    is: TiposDeCabecalhoMensagemInterativaEnum.VIDEO,
    then: schema =>
      schema
        .url('A URL deve ser válida')
        .required('A URL do video do cabeçalho é obrigatória'),
    otherwise: schema => schema.strip(),
  }),

  documentoCabecalho: Yup.string().when('tipoCabecalho', {
    is: TiposDeCabecalhoMensagemInterativaEnum.DOCUMENTO,
    then: schema =>
      schema
        .url('A URL deve ser válida')
        .required('A URL do documento do cabeçalho é obrigatória'),
    otherwise: schema => schema.strip(),
  }),

  mensagem: Yup.string()
    .max(1024, 'O limite máximo de caracteres é de 1024 caracteres')
    .required('A mensagem é obrigatória'),

  rodape: Yup.string().max(
    60,
    'O limite máximo de caracteres é de 60 caracteres',
  ),

  rotuloDoBotao: Yup.string()
    .max(20, 'O limite máximo de caracteres é de 20 caracteres')
    .required('O rótulo do botão é obrigatório'),

  urlCta: Yup.string()
    .url('A URL deve ser válida')
    .required('A URL é obrigatória'),
});

const textVariableSchema = Yup.object().shape({
  name: Yup.string().optional(),
  value: Yup.string()
    .min(1, 'Este campo é obrigatório.')
    .required('Este campo é obrigatório.'),
});

const headerVariablesSchema = Yup.object().shape({
  type: Yup.string()
    .oneOf(Object.values(FormatosDeCabecalhoMensagemTemplateEnum))
    .nullable()
    .optional(),
  url: Yup.string().when('type', {
    is: (type: string) =>
      [
        MessageHeaderFormatsTemplate.IMAGEM,
        MessageHeaderFormatsTemplate.VIDEO,
        MessageHeaderFormatsTemplate.DOCUMENTO,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ].includes(type as any),
    then: schema => schema.required('O arquivo é obrigatório'),
    otherwise: schema => schema.optional(),
  }),
  storage_id: Yup.string().optional(),
});

const bodyVariablesSchema = Yup.object().shape({
  variables: Yup.array().of(textVariableSchema).optional(),
});

export const messageVariablesSchema = Yup.object().shape({
  header: headerVariablesSchema.optional(),
  headerVariables: Yup.array().of(textVariableSchema).optional(),
  headerFile: Yup.mixed()
    .nullable()
    .optional()
    .test(
      'fileSize',
      'Arquivo muito grande',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      function validateFileSize(this: any, value: any) {
        if (!value || !(value instanceof File)) return true;
        const type = this.parent.header?.type;
        if (!type) return true;

        const maxSize = MAX_FILE_SIZES[type as keyof typeof MAX_FILE_SIZES];
        return !maxSize || value.size <= maxSize;
      },
    ),
  body: bodyVariablesSchema.optional(),
});

export interface MessageVariableItem {
  name?: string;
  value: string;
}

export interface MessageVariableHeader {
  type?: string | null;
  url?: string;
  storage_id?: string;
}

export interface MessageVariableBody {
  variables?: MessageVariableItem[];
}

export interface MessageVariables {
  header?: MessageVariableHeader;
  headerVariables?: MessageVariableItem[];
  headerFile?: File | null;
  body?: MessageVariableBody;
}
